/**
 * @fileoverview Encryption Context Provider
 *
 * Manages encryption session state with automatic IndexedDB persistence.
 * Provides session restoration on app load and handles unlock/setup flows.
 *
 * **Key Features:**
 * - Automatic session restoration from IndexedDB on mount
 * - In-memory caching for fast operations
 * - Configurable auto-prompt for unlock dialogs
 * - Seamless WebAuthn and password unlock support
 *
 * @module @xanectech/encryption/contexts
 */

import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
  type ReactNode,
  JSX,
} from 'react';
import { createLogger } from '@xanectech/lib';
import {
  isSessionInitialized,
  tryRestoreSession,
  clearSession,
} from '../services/SessionKeyManager';

const logger = createLogger('EncryptionContext');

/**
 * Encryption context state and actions
 */
export interface EncryptionContextType {
  /** Whether the encryption session is fully initialized and ready for operations */
  isEncryptionReady: boolean;
  /** Whether the session is currently unlocked (master key in memory) */
  isUnlocked: boolean;
  /** Whether the user needs to unlock (has master key but session not initialized) */
  needsUnlock: boolean;
  /** Whether the user needs to set up encryption (no master key exists) */
  needsSetup: boolean;
  /** Whether the user has a master key stored in the database */
  hasMasterKey: boolean | null;
  /** Whether session restoration is in progress */
  isRestoring: boolean;
  /** Trigger the unlock prompt */
  showUnlockPrompt: () => void;
  /** Hide the unlock prompt */
  hideUnlockPrompt: () => void;
  /** Lock the encryption session (clear all cached keys) */
  lock: () => void;
  /** Manually trigger session restoration from IndexedDB */
  restoreSession: () => Promise<boolean>;
  /** Update unlock status after successful unlock */
  setUnlocked: (unlocked: boolean) => void;
  /** Update master key status */
  setHasMasterKey: (hasKey: boolean) => void;
}

const EncryptionContext = createContext<EncryptionContextType | undefined>(
  undefined,
);

/**
 * Hook to access the encryption context
 * @throws Error if used outside EncryptionProvider
 */
export function useEncryptionContext(): EncryptionContextType {
  const context = useContext(EncryptionContext);
  if (context === undefined) {
    throw new Error(
      'useEncryptionContext must be used within an EncryptionProvider',
    );
  }
  return context;
}

export interface EncryptionProviderProps {
  children: ReactNode;
  /** User ID for the current authenticated user */
  userId: string | null;
  /** Whether to automatically show unlock prompts */
  autoPrompt?: boolean;
  /** Callback to check if master key exists in database */
  checkHasMasterKey?: () => Promise<boolean>;
  /** Callback when unlock prompt should be shown */
  onShowUnlockPrompt?: () => void;
  /** Callback when setup prompt should be shown */
  onShowSetupPrompt?: () => void;
}

/**
 * Encryption Provider Component
 *
 * Wraps the application to provide encryption session management.
 * Automatically attempts to restore session from IndexedDB on mount.
 *
 * @example
 * ```tsx
 * <EncryptionProvider
 *   userId={user?.id ?? null}
 *   checkHasMasterKey={async () => { ... }}
 *   onShowUnlockPrompt={() => setShowUnlock(true)}
 * >
 *   <App />
 * </EncryptionProvider>
 * ```
 */
export function EncryptionProvider({
  children,
  userId,
  autoPrompt = true,
  checkHasMasterKey,
  onShowUnlockPrompt,
  onShowSetupPrompt,
}: EncryptionProviderProps): JSX.Element {
  const [isUnlocked, setIsUnlocked] = useState(false);
  const [hasMasterKey, setHasMasterKey] = useState<boolean | null>(null);
  const [isRestoring, setIsRestoring] = useState(false);
  const [showPrompt, setShowPrompt] = useState(false);

  /**
   * Attempt to restore session from IndexedDB
   */
  const restoreSession = useCallback(async (): Promise<boolean> => {
    if (!userId) {
      return false;
    }

    setIsRestoring(true);
    try {
      const restored = await tryRestoreSession(userId);

      if (restored) {
        logger.info('Session restored from IndexedDB');
        setIsUnlocked(true);
        return true;
      } else {
        return false;
      }
    } catch (error) {
      logger.error('Failed to restore session', error as Error);
      return false;
    } finally {
      setIsRestoring(false);
    }
  }, [userId]);

  /**
   * Initialize encryption session on mount or user change
   *
   * Flow:
   * 1. Check if already unlocked in memory
   * 2. Try to restore from IndexedDB
   * 3. Check if master key exists in database
   * 4. Show appropriate prompts if autoPrompt enabled
   */
  useEffect(() => {
    if (!userId) {
      // User logged out - reset state
      setIsUnlocked(false);
      setHasMasterKey(null);
      setShowPrompt(false);
      return;
    }

    const initializeSession = async () => {
      setIsRestoring(true);

      try {
        // Check if already unlocked in memory
        let unlocked = isSessionInitialized(userId);

        if (!unlocked) {
          // Try to restore from IndexedDB
          unlocked = await restoreSession();
        }

        setIsUnlocked(unlocked);

        // Check if master key exists in database
        if (checkHasMasterKey) {
          try {
            const hasKey = await checkHasMasterKey();
            setHasMasterKey(hasKey);

            // Show prompts if needed
            if (autoPrompt && !unlocked) {
              if (hasKey) {
                setShowPrompt(true);
                onShowUnlockPrompt?.();
              } else {
                onShowSetupPrompt?.();
              }
            }
          } catch (error) {
            logger.error('Failed to check master key', error as Error);
            setHasMasterKey(null);
          }
        }
      } finally {
        setIsRestoring(false);
      }
    };

    void initializeSession();
  }, [
    userId,
    autoPrompt,
    checkHasMasterKey,
    restoreSession,
    onShowUnlockPrompt,
    onShowSetupPrompt,
  ]);

  /**
   * Lock the encryption session
   */
  const lock = useCallback(() => {
    logger.info('Locking encryption session');
    clearSession();
    setIsUnlocked(false);
  }, []);

  /**
   * Show the unlock prompt
   */
  const showUnlockPrompt = useCallback(() => {
    if (userId && !isUnlocked) {
      setShowPrompt(true);
      onShowUnlockPrompt?.();
    }
  }, [userId, isUnlocked, onShowUnlockPrompt]);

  /**
   * Hide the unlock prompt
   */
  const hideUnlockPrompt = useCallback(() => {
    setShowPrompt(false);
  }, []);

  /**
   * Update unlock status (called after successful unlock)
   */
  const handleSetUnlocked = useCallback((unlocked: boolean) => {
    setIsUnlocked(unlocked);
    if (unlocked) {
      setShowPrompt(false);
    }
  }, []);

  const contextValue = useMemo<EncryptionContextType>(
    () => ({
      isEncryptionReady: isUnlocked,
      isUnlocked,
      needsUnlock: !!userId && hasMasterKey === true && !isUnlocked,
      needsSetup: !!userId && hasMasterKey === false,
      hasMasterKey,
      isRestoring,
      showUnlockPrompt,
      hideUnlockPrompt,
      lock,
      restoreSession,
      setUnlocked: handleSetUnlocked,
      setHasMasterKey,
    }),
    [
      isUnlocked,
      userId,
      hasMasterKey,
      isRestoring,
      showUnlockPrompt,
      hideUnlockPrompt,
      lock,
      restoreSession,
      handleSetUnlocked,
    ],
  );

  return (
    <EncryptionContext.Provider value={contextValue}>
      {children}
    </EncryptionContext.Provider>
  );
}

export default EncryptionContext;
